import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModal from "../../components/Modals/bulk-repayment/ConfirmationModal";
import ValidationModal from "../../components/Modals/bulk-repayment/ValidationModal";
import ValidationFailedModal from "../../components/Modals/bulk-repayment/ValidationFailedModal";
import ValidationSuccessModal from "../../components/Modals/bulk-repayment/ValidationSuccessModal";

const BulkRepayments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isValidationOpen, setIsValidationOpen] = useState(false);
  const [validationSuccessOpen, setValidationSuccessOpen] = useState(false);
  const [isValidationFailedOpen, setIsValidationFailedOpen] = useState(false);
  const [repayments, setRepayments] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [displayedRepayments, setDisplayedRepayments] = useState([]);

  useEffect(() => {
    let data = location.state?.response?.data;

    if (!data) {
      const storedData = localStorage.getItem("bulkRepaymentData");
      if (storedData) {
        data = JSON.parse(storedData);
      }
    }

    console.log("Retrieved Repayments Data:", data);

    if (!data || !Array.isArray(data)) {
      console.error("Data is missing or not an array:", data);
      return;
    }

    const groupedRepayments = Object.values(
      data?.reduce((acc, curr) => {
        const {
          MemberNo,
          Name,
          SavingsShares,
          Investment,
          RiskFund,
          NormalLoan,
          EmergencyLoan,
          TopupLoan,
          valid,
        } = curr;

        if (!acc[MemberNo]) {
          acc[MemberNo] = {
            MemberNo: MemberNo,
            Name: Name || "Unknown",
            savingsShares: Number(SavingsShares) || 0,
            investment: Number(Investment) || 0,
            riskFund: Number(RiskFund) || 0,
            normalLoan: Number(NormalLoan) || 0,
            emergencyLoan: Number(EmergencyLoan) || 0,
            topupLoan: Number(TopupLoan) || 0,
            valid: valid !== undefined ? valid : true,
            reasons: Array.isArray(curr.reasons) ? curr.reasons : [],
          };
        }

        return acc;
      }, {}),
    );

    console.log("Processed Repayments Data:", groupedRepayments);
    setRepayments(groupedRepayments);
    setTotalPages(Math.ceil(groupedRepayments.length / limit));
  }, [location.state]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    setDisplayedRepayments(repayments.slice(startIndex, endIndex));
    setTotalPages(Math.ceil(repayments.length / limit));
  }, [currentPage, limit, repayments]);

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPages = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <div
            key={i}
            className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
              currentPage === i
                ? "bg-[#074073] text-white"
                : "bg-[#F5F5F5] text-[#074073]"
            }`}
            onClick={() => handlePageChange(i)}
          >
            <p>{i}</p>
          </div>,
        );
      }
    } else {
      pages.push(
        <div
          key={1}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === 1
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(1)}
        >
          <p>1</p>
        </div>,
      );

      if (currentPage <= maxVisiblePages) {
        for (let i = 2; i <= maxVisiblePages + 1; i++) {
          pages.push(
            <div
              key={i}
              className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                currentPage === i
                  ? "bg-[#074073] text-white"
                  : "bg-[#F5F5F5] text-[#074073]"
              }`}
              onClick={() => handlePageChange(i)}
            >
              <p>{i}</p>
            </div>,
          );
        }
        pages.push(
          <div
            key="ellipsis-end"
            className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center bg-[#F5F5F5] text-[#074073]"
          >
            <p>...</p>
          </div>,
        );
      } else if (currentPage > totalPages - maxVisiblePages) {
        pages.push(
          <div
            key="ellipsis-start"
            className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center bg-[#F5F5F5] text-[#074073]"
          >
            <p>...</p>
          </div>,
        );
        for (let i = totalPages - maxVisiblePages; i < totalPages; i++) {
          pages.push(
            <div
              key={i}
              className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                currentPage === i
                  ? "bg-[#074073] text-white"
                  : "bg-[#F5F5F5] text-[#074073]"
              }`}
              onClick={() => handlePageChange(i)}
            >
              <p>{i}</p>
            </div>,
          );
        }
      } else {
        pages.push(
          <div
            key="ellipsis-start"
            className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center bg-[#F5F5F5] text-[#074073]"
          >
            <p>...</p>
          </div>,
        );
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <div
              key={i}
              className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                currentPage === i
                  ? "bg-[#074073] text-white"
                  : "bg-[#F5F5F5] text-[#074073]"
              }`}
              onClick={() => handlePageChange(i)}
            >
              <p>{i}</p>
            </div>,
          );
        }
        pages.push(
          <div
            key="ellipsis-end"
            className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center bg-[#F5F5F5] text-[#074073]"
          >
            <p>...</p>
          </div>,
        );
      }

      pages.push(
        <div
          key={totalPages}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === totalPages
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(totalPages)}
        >
          <p>{totalPages}</p>
        </div>,
      );
    }

    return pages;
  };

  const handleApproveAll = () => {
    setIsConfirmationOpen(true);
  };
  const handleBack = () => {
    navigate("/dashboard/bulk-repayment");
  };

  const handleConfirm = () => {
    setIsValidationOpen(true);
    setIsConfirmationOpen(false);

    setTimeout(() => {
      if (validateRepayments(repayments)) {
        setIsValidationOpen(false);
        setIsValidationFailedOpen(true);
      } else {
        setIsValidationOpen(false);
        setValidationSuccessOpen(true);
      }
    }, 2000);
  };

  const validateRepayments = (repayments) => {
    return repayments?.some(
      (repayment) =>
        repayment?.valid === false ||
        !repayment?.MemberNo ||
        !repayment?.Name ||
        repayment?.savingsShares == null ||
        repayment?.investment == null ||
        repayment?.riskFund == null ||
        repayment?.normalLoan == null ||
        repayment?.emergencyLoan == null ||
        repayment?.topupLoan == null,
    );
  };

  const handleCheckErrors = () => {
    navigate("/dashboard/bulk-repayment/preview", {
      state: { showErrors: true },
    });
    setIsValidationFailedOpen(false);
    setShowErrors(true);
  };

  const handlePreview = () => {
    setValidationSuccessOpen(false);
    navigate("/dashboard/bulk-repayment/update-records", {
      state: { repayments },
    });
  };

  return (
    <div className="p-6 bg-white min-h-screen flex flex-col overflow-hidden">
      <h2 className="text-xl uppercase font-bold text-green-800">
        Bulk Repayments
      </h2>
      <p className="font-semibold text-gray-700">Preview Repayment Updates</p>
      <p className="text-gray-600 mb-4">
        Review the records before finalizing the update to ensure accuracy.
      </p>

      <div className="flex-1 flex flex-col">
        <div className="w-full h-[380px] flex flex-col">
          <div className="h-[44px] w-full text-[14px] px-[20px] flex items-center bg-[#E3F8FF] font-semibold">
            <p className="flex-[1] !m-0 !p-0">MEMBER ID</p>
            <p className="flex-[2] !m-0 !p-0">NAME OF MEMBER</p>
            <p className="flex-[1] !m-0 !p-0">SAVINGSSHARES</p>
            <p className="flex-[1] !m-0 !p-0">INVESTMENT</p>
            <p className="flex-[1] !m-0 !p-0">RISKFUND</p>
            <p className="flex-[1] !m-0 !p-0">EMERGENCY LOAN</p>
            <p className="flex-[1] !m-0 !p-0">NORMAL LOAN</p>
            <p className="flex-[1] !m-0 !p-0">TOPUP LOAN</p>
          </div>

          <div className="max-h-[700px] overflow-y-auto">
            {displayedRepayments?.length > 0 ? (
              displayedRepayments?.map((repayment, index) => (
                <div
                  key={index}
                  className={`h-[44px] w-full px-[20px] flex items-center mt-[2px] ${
                    showErrors && repayment?.valid === false
                      ? "border border-red-500"
                      : ""
                  }`}
                >
                  <p className="flex-[1] text-[#06B217]">
                    {repayment?.MemberNo}
                  </p>
                  <p className="flex-[2]">{repayment?.Name}</p>
                  <p className="flex-[1]">
                    KES{" "}
                    {Number(repayment?.savingsShares)?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                  <p className="flex-[1]">
                    KES{" "}
                    {Number(repayment?.investment)?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                  <p className="flex-[1]">
                    KES{" "}
                    {Number(repayment?.riskFund)?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                  <p className="flex-[1]">
                    KES{" "}
                    {Number(repayment?.emergencyLoan)?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                  <p className="flex-[1]">
                    KES{" "}
                    {Number(repayment?.normalLoan)?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                  <p className="flex-[1]">
                    KES{" "}
                    {Number(repayment?.topupLoan)?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-600 py-4">Data not picked</p>
            )}
          </div>

          <div className="h-[70px] bg-white w-[97%] px-[20px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-[20px]">
              <p className="text-[#074073] text-[14px]">Records per page</p>
              <select
                name="page"
                id="page"
                className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="50">50</option>
                <option value="30">30</option>
                <option value="20">20</option>
                <option value="10">10</option>
              </select>
              <p className="text-[#074073] text-[14px]">
                Page {currentPage} / {totalPages} ({repayments?.length} records)
              </p>
            </div>
            <div className="flex items-center gap-[15px]">
              <p
                onClick={() => handlePageChange(Math?.max(currentPage - 1, 1))}
                className="text-[14px] cursor-pointer text-[#074073]"
              >
                Prev
              </p>
              {renderPages()}
              <p
                onClick={() =>
                  handlePageChange(Math?.min(currentPage + 1, totalPages))
                }
                className="text-[14px] cursor-pointer text-[#074073]"
              >
                Next
              </p>
            </div>
          </div>
        </div>
        {showErrors && (
          <div className="mt-2 mb-2 p-4 bg-red-50 rounded-lg">
            <h3 className="text-red-700 font-bold mb-3">Validation Errors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {displayedRepayments
                ?.filter((repayment) => !repayment?.valid)
                ?.map((repayment, index) => (
                  <div
                    key={index}
                    className="bg-white p-3 rounded border border-red-200"
                  >
                    <p className="font-semibold text-red-800">
                      {repayment?.Name} ({repayment?.MemberNo})
                    </p>
                    {repayment?.reasons?.length > 0 ? (
                      <ul className="list-disc list-inside pl-4 mt-1 space-y-1">
                        {repayment?.reasons?.map((reason, i) => (
                          <li key={i} className="text-red-600 text-sm">
                            {reason}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-red-600 text-sm">
                        No specific reason provided.
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <button
            onClick={handleBack}
            className="h-[51px] w-[404px] bg-[#074073] text-white font-bold rounded-lg"
          >
            Go Back
          </button>
          <div>
            <button
              onClick={handleApproveAll}
              className="h-[51px] w-[404px] bg-[#074073] text-white font-bold rounded-lg"
            >
              Approve All
            </button>

            <ConfirmationModal
              isOpen={isConfirmationOpen}
              onClose={() => setIsConfirmationOpen(false)}
              onConfirm={handleConfirm}
            />

            <ValidationModal
              isOpen={isValidationOpen}
              onClose={() => setIsValidationOpen(false)}
            />
            {validationSuccessOpen && (
              <ValidationSuccessModal
                onClose={() => setValidationSuccessOpen(false)}
                onClick={handlePreview}
              />
            )}
            {isValidationFailedOpen && (
              <ValidationFailedModal
                onClose={() => setIsValidationFailedOpen(false)}
                onClick={handleCheckErrors}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkRepayments;
